import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { take } from 'lodash';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import Modal from '~/components/base/Modal';
import Button from '~/components/base/Button';
import Icon from '~/components/base/Icon';

import ModalCard from '~/components/modal/ModalCard';

import { useLocationPickerContext } from '~/context/LocationPickerContext';

import { h2, error } from '~/styles/typography';
import { input } from '~/styles/forms';
import { srOnly } from '~/styles/helpers';

const Error = styled.p`
    ${error};
    color: ${({ theme }) => theme.colors.text.red};
    margin-bottom: 15px;
`;

export const ModalTitle = styled.h2`
    ${h2};
    color: ${({ theme }) => theme.colors.primary.red};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        margin-bottom: 45px;
    }
`;

const StyledButton = styled(Button)`
    display: block;
    margin-top: ${({ marginTopMobile }) => (marginTopMobile ? `15px` : `0`)};
    text-align: center;
    width: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        margin-top: 45px;
    }
`;

const ZipWrapper = styled.form`
    position: relative;
    margin-bottom: 45px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        margin-bottom: 0;
    }
`;

const Label = styled.label`
    ${srOnly};
`;

const Input = styled.input`
    ${input};
    border: 2px solid ${({ theme }) => theme.colors.primary.yellow};
    background-color: ${({ theme }) => theme.colors.primary.white};
`;

const SubmitButton = styled.button`
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    appearance: none;
    border: none;
    background-color: transparent;
`;

const LocationPickerModal = ({
    open,
    setOpen,
    selectedLocation,
    setLocation
}) => {
    const { address, setManualLocation, locations } =
        useLocationPickerContext();
    const [newSelectedLocation, setNewSelectedLocation] =
        useState(selectedLocation);
    const [error, setError] = useState(false);

    const onConfirm = () => {
        if (newSelectedLocation) {
            setLocation(newSelectedLocation);
            setOpen(false);
        } else {
            setError(true);
        }
    };

    const inputRef = useRef(null);

    const onSubmit = (event) => {
        event.preventDefault();
        if (inputRef.current) {
            const address = inputRef.current.value;
            setManualLocation(address);
        }
    };

    const selectableLocations = locations.filter(
        ({ comingSoon }) => !comingSoon
    );

    return (
        <Modal
            {...{ open, setOpen }}
            onClose={() => setNewSelectedLocation(selectedLocation)}
        >
            <Row>
                <Column span={12} spanTablet={6} spanLargeTablet={8}>
                    <ModalTitle>Where do you want to get your wash?</ModalTitle>
                </Column>
                <Column
                    span={12}
                    spanTablet={6}
                    startLargeTablet={10}
                    spanLargeTablet={3}
                >
                    <ZipWrapper onSubmit={onSubmit}>
                        <Label htmlFor="modal-zip-input">Enter ZIP code</Label>
                        <Input
                            id="modal-zip-input"
                            placeholder="Enter ZIP code"
                            type="text"
                            defaultValue={address}
                            ref={inputRef}
                        />
                        <SubmitButton type="submit">
                            <Icon icon="search" size={20} color="black" />
                        </SubmitButton>
                    </ZipWrapper>
                </Column>
            </Row>
            {error && (
                <Row>
                    <Column span={12}>
                        <Error>Please select a location</Error>
                    </Column>
                </Row>
            )}
            <Row>
                {take(selectableLocations, 4).map((location) => (
                    <Column span={12} spanTablet={6} key={location.id}>
                        <ModalCard
                            title={location.address.city}
                            description={`${location.address.line1}, ${location.address.city}, ${location.address.region} ${location.address.postalCode}`}
                            chipText={`${Math.round(location.distance)} miles away`}
                            largeTitle={true}
                            inputName="location"
                            onChange={() => setNewSelectedLocation(location)}
                            checked={newSelectedLocation?.id === location.id}
                        />
                    </Column>
                ))}
            </Row>
            <Row>
                <Column span={12} spanTablet={6}>
                    <StyledButton onClick={onConfirm} marginTopMobile>
                        Confirm
                    </StyledButton>
                </Column>
                <Column span={12} spanTablet={6}>
                    <StyledButton
                        href="/locations"
                        onClick={() => setOpen(false)}
                    >
                        View all Locations
                    </StyledButton>
                </Column>
            </Row>
        </Modal>
    );
};

export default LocationPickerModal;
